import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Menu, Bell, Sun, Moon, Search, RotateCw } from 'lucide-react';
import { cn } from '../../lib/cn';
import { toggleTheme, selectThemeMode } from '../../store/slices/themeSlice';
import { selectUser } from '../../store/slices/authSlice';
import { Avatar } from '../shared/AvatarWithInitials';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { useLocation } from 'react-router-dom';
import { CommandPalette } from '../search/CommandPalette';

const TITLES = {
  '/dashboard':'Dashboard','/employees':'Employees','/attendance':'Attendance',
  '/payroll':'Payroll','/leave':'Leave Management','/expense':'Expense & Khata',
  '/recruitment':'Recruitment','/compliance':'Compliance','/reports':'Reports',
  '/chat':'Chat','/lens':'Lens Devices','/roster':'Roster','/settings':'Settings',
};

export function Topbar({ onMenuClick }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const theme = useSelector(selectThemeMode);
  const user = useSelector(selectUser);
  const { pathname } = useLocation();
  const title = TITLES['/'+pathname.split('/')[1]] || 'AttendX';
  const [refreshing, setRefreshing] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Keyboard shortcut: Cmd+K / Ctrl+K opens the palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Refetches every active query (attendance, employees, payroll, etc.) in
  // place — no full browser reload, and (unlike a real F5) never touches
  // the session, so it can't ever log the user out.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries();
      toast.success('Data refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
    <header className="h-14 bg-[var(--bg-card)] border-b border-[var(--border)] flex items-center px-4 gap-3 shrink-0">
      <button onClick={onMenuClick} className="lg:hidden w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
        <Menu size={20}/>
      </button>
      <h2 className="text-[16px] font-semibold text-[var(--text-primary)] flex-1 truncate">{title}</h2>
      <WorkspaceSwitcher />
      <div className="flex items-center gap-1">
        <button onClick={handleRefresh} disabled={refreshing} title="Refresh data"
          className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors disabled:opacity-50">
          <RotateCw size={17} className={cn(refreshing && 'animate-spin')}/>
        </button>
        <button onClick={() => setSearchOpen(true)} title="Search (⌘K)"
          className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
          <Search size={18}/>
        </button>
        <button className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors relative">
          <Bell size={18}/>
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[var(--danger)] rounded-full"/>
        </button>
        <button onClick={()=>dispatch(toggleTheme())} className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
          {theme==='dark'?<Sun size={18}/>:<Moon size={18}/>}
        </button>
        <Avatar name={user?.name} size="sm" className="ml-1 cursor-pointer"/>
      </div>
    </header>
    <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
export default Topbar;
